import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, Check, Copy, Send, Monitor } from 'lucide-react';
import { api } from '../../services/apiService';
import { dealInvitesApi, type DealInvite } from '../../services/dealsApi';
import { effectiveDealPriceCents } from '../../services/dealPricing';
import { getIcon } from '../../services/iconRegistry';
import { RubyGemMark } from '../RubyGemMark';

/**
 * SEND A DEAL — replaces the old "New Tenant" bottom drawer.
 *
 * Approved mockup: `applications/platform-app/mockups/deal-invite-offer-page.html` (screen ①).
 *
 * NOTHING IS CREATED HERE. The operator names a prospect and picks an active
 * deal; the server stores a DealInvite, emails a personal link to the offer page
 * (platform-app `/#/offer/<token>`), and the prospect's OWN signup — the
 * existing deal-code path — creates the tenant. The invite only records how far
 * they got (sent → opened → signed up).
 *
 * Discounts are the DEAL's. A special price is a deal variant, never a number
 * typed into this dialog.
 *
 * SIZED TO THE VIEWPORT: the dialog never exceeds the screen. Header and footer
 * are fixed; the form and the preview rail scroll independently if they must.
 */

interface DealLike {
  id: string;
  code: string;
  name: string;
  monthlyAmount?: number;
  priceCents?: number;
  packageIds?: string[];
  deviceSkuIds?: string[];
  devicePackageIds?: string[];
  isDefaultDeal?: boolean;
}

interface LeadLike {
  id?: string;
  businessName?: string;
  name?: string;
  contactName?: string;
  email?: string;
  phone?: string;
}

/** A package as the packages catalog stores it — its OWN icon and colours. */
interface PackageRecord {
  id: string;
  name?: string;
  icon?: string;
  colorType?: 'solid' | 'gradient';
  color?: string;
  gradientFrom?: string;
  gradientTo?: string;
}

interface Props {
  open: boolean;
  deals: DealLike[];
  lead: LeadLike | null;
  onClose: () => void;
  onSent: (invite: DealInvite) => void;
}

const EXPIRY_CHOICES: { days: number | null; label: string }[] = [
  { days: 7, label: '7 days' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
  { days: null, label: 'Deal end' },
];

const money = (cents?: number | null) =>
  typeof cents === 'number'
    ? `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The same fill the Packages page and the tenant profile paint a package with. */
export const packageFill = (pkg?: PackageRecord): React.CSSProperties =>
  pkg?.colorType === 'gradient'
    ? { background: `linear-gradient(135deg, ${pkg.gradientFrom || '#e8365a'}, ${pkg.gradientTo || '#ec4899'})` }
    : { backgroundColor: pkg?.color || '#64748b' };

/** Compact floating-label input — the platform-app signup's field, one size down. */
const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  optional?: boolean;
  multiline?: boolean;
  invalid?: boolean;
}> = ({ label, value, onChange, type = 'text', optional, multiline, invalid }) => {
  const base = `peer w-full rounded-xl border bg-white px-3.5 pt-[22px] pb-1.5 text-sm text-stone-900 outline-none transition
    focus:border-stone-900 focus:ring-[3px] focus:ring-stone-900/[0.07]
    ${invalid ? 'border-red-400' : 'border-stone-200 hover:border-stone-300'}`;
  return (
    <label className="relative block">
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className={`${base} resize-none leading-snug`} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={`${base} h-[50px]`} />
      )}
      <span className="pointer-events-none absolute left-3.5 top-2 text-[10px] font-semibold uppercase tracking-wider text-stone-400 peer-focus:text-[#DC143C]">
        {label}
      </span>
      {optional && <span className="pointer-events-none absolute right-3.5 top-2 text-[10px] text-stone-400">optional</span>}
    </label>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div className="mb-2 flex items-center justify-between gap-2">
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">{children}</p>
    {right}
  </div>
);

const SendDealModal: React.FC<Props> = ({ open, deals, lead, onClose, onSent }) => {
  const [contactName, setContactName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dealId, setDealId] = useState<string>('');
  const [dealSearch, setDealSearch] = useState('');
  const [expiryDays, setExpiryDays] = useState<number | null>(14);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState<'send' | 'copy' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [catalog, setCatalog] = useState<Record<string, PackageRecord> | null>(null);
  const [devices, setDevices] = useState<string[] | null>(null);
  const [railTab, setRailTab] = useState<'page' | 'email'>('page');

  // Reset every time the dialog opens — from a lead, or blank.
  useEffect(() => {
    if (!open) return;
    setContactName(lead?.contactName || lead?.name || '');
    setBusinessName(lead?.businessName || '');
    setEmail(lead?.email || '');
    setPhone(lead?.phone || '');
    setNote('');
    setExpiryDays(14);
    setDealSearch('');
    setError(null);
    setCopied(false);
    setRailTab('page');
    const fallback = deals.find((d) => d.isDefaultDeal) || deals[0];
    setDealId(fallback?.id || '');
  }, [open, lead, deals]);

  // Esc closes, like every other dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sending) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, sending, onClose]);

  // The packages catalog — each package's OWN icon and colours. Loaded once.
  useEffect(() => {
    if (!open || catalog) return;
    api.packages.listVisible()
      .then((res) => {
        const map: Record<string, PackageRecord> = {};
        for (const p of Array.isArray(res?.packages) ? res.packages : []) if (p?.id) map[p.id] = p;
        setCatalog(map);
      })
      .catch(() => setCatalog({}));
  }, [open, catalog]);

  const deal = useMemo(() => deals.find((d) => d.id === dealId) || null, [deals, dealId]);

  // Device names come from the public deal lookup — what the prospect will see.
  useEffect(() => {
    if (!open || !deal?.code) { setDevices(null); return; }
    let live = true;
    setDevices(null);
    api.deals.getDealByCode(deal.code)
      .then((res) => {
        if (!live) return;
        const list = Array.isArray(res?.deal?.devices) ? res.deal.devices : [];
        setDevices(list.map((x: any) => x?.name).filter(Boolean));
      })
      .catch(() => { if (live) setDevices([]); });
    return () => { live = false; };
  }, [open, deal?.code]);

  const dealPackages: PackageRecord[] = useMemo(
    () => (deal?.packageIds || []).map((id) => catalog?.[id] || { id, name: id }),
    [deal, catalog],
  );

  const filteredDeals = useMemo(() => {
    const q = dealSearch.trim().toLowerCase();
    return q ? deals.filter((d) => d.name?.toLowerCase().includes(q) || d.code?.toLowerCase().includes(q)) : deals;
  }, [deals, dealSearch]);

  const expiresAt = expiryDays ? Date.now() + expiryDays * 86_400_000 : null;
  const emailOk = EMAIL_RE.test(email.trim());
  const ready = !!contactName.trim() && !!businessName.trim() && emailOk && !!deal;
  const firstName = contactName.trim().split(/\s+/)[0] || 'there';
  const priceCents = deal ? effectiveDealPriceCents(deal) : null;

  const submit = async (mode: 'send' | 'copy') => {
    if (!ready || !deal) {
      setError(!emailOk && email ? 'That email address does not look right.' : 'Name, business, email and a deal are required.');
      return;
    }
    setSending(mode);
    setError(null);
    try {
      const { invite } = await dealInvitesApi.create({
        contactName: contactName.trim(),
        businessName: businessName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || undefined,
        dealId: deal.id,
        note: note.trim() || undefined,
        expiresInDays: expiryDays ?? undefined,
        leadId: lead?.id,
        sendEmail: mode === 'send',
      });
      if (mode === 'copy' && invite.url) {
        await navigator.clipboard.writeText(invite.url).catch(() => undefined);
        setCopied(true);
      }
      onSent(invite);
    } catch (e: any) {
      setError(e?.message || 'The invite could not be created.');
    } finally {
      setSending(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-stone-950/40 p-3 backdrop-blur-[2px] md:p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Send a deal"
        className="flex max-h-[calc(100vh-24px)] w-full max-w-[940px] flex-col overflow-hidden rounded-[22px] bg-white shadow-[0_40px_90px_-30px_rgba(10,10,11,0.55)] ring-1 ring-black/5 md:max-h-[min(720px,calc(100vh-48px))]"
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="flex items-center gap-3 border-b border-stone-100 px-5 py-3.5">
          <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-[#FFF1F3]">
            <RubyGemMark size={20} color="#DC143C" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-light leading-tight tracking-[-0.03em] text-stone-900">
              Send a <span className="font-semibold">deal</span>
            </h2>
            <p className="truncate text-xs text-stone-500">A personal offer page — the account is created when they sign up.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto grid h-11 w-11 flex-none place-items-center rounded-xl border border-stone-200 text-stone-500 hover:bg-stone-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="grid min-h-0 flex-1 md:grid-cols-[1fr_320px]">
          {/* Form */}
          <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-5 py-4 [&>*]:shrink-0">
            <div>
              <SectionLabel
                right={lead ? (
                  <span className="truncate rounded-full border border-stone-200 bg-stone-50 px-2.5 py-0.5 text-[11px] text-stone-500">
                    From lead · {lead.businessName}
                  </span>
                ) : null}
              >
                Who it's for
              </SectionLabel>
              <div className="grid gap-2.5 sm:grid-cols-2">
                <Field label="Contact name" value={contactName} onChange={setContactName} />
                <Field label="Business name" value={businessName} onChange={setBusinessName} />
                <Field label="Email" type="email" value={email} onChange={setEmail} invalid={!!email && !emailOk} />
                <Field label="Phone" type="tel" value={phone} onChange={setPhone} optional />
              </div>
            </div>

            <div>
              <SectionLabel right={<span className="font-mono text-[10px] uppercase tracking-[0.16em] text-stone-400">{deals.length} active</span>}>
                Deal
              </SectionLabel>
              <div className="flex h-11 items-center gap-2 rounded-xl border border-stone-200 bg-stone-50 px-3 text-stone-400 focus-within:border-stone-900">
                <Search size={15} />
                <input
                  value={dealSearch}
                  onChange={(e) => setDealSearch(e.target.value)}
                  placeholder="Search deals by name or code…"
                  className="h-10 flex-1 bg-transparent text-sm text-stone-900 outline-none placeholder:text-stone-400"
                />
              </div>
              <div className="mt-2 grid max-h-[150px] gap-2 overflow-y-auto pr-0.5 sm:grid-cols-2">
                {filteredDeals.length === 0 && (
                  <p className="col-span-full rounded-xl border border-dashed border-stone-200 p-3 text-sm text-stone-500">
                    No active deal matches "{dealSearch}".
                  </p>
                )}
                {filteredDeals.map((d) => {
                  const sel = d.id === dealId;
                  const pk = d.packageIds?.length ?? 0;
                  return (
                    <button
                      type="button"
                      key={d.id}
                      onClick={() => setDealId(d.id)}
                      className={`flex min-h-[64px] items-center gap-3 rounded-xl border bg-white px-3 py-2 text-left transition ${
                        sel ? 'border-stone-900 shadow-[0_0_0_1px_#0B0B0C]' : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <span className={`grid h-5 w-5 flex-none place-items-center rounded-full border ${sel ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-300'}`}>
                        {sel && <Check size={12} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-semibold text-stone-900">{d.name}</span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-stone-400">
                          <span className="rounded border border-[#DC143C]/40 px-1 font-mono text-[10px] text-[#DC143C]">{d.code}</span>
                          {pk} pkg{pk === 1 ? '' : 's'}
                        </span>
                      </span>
                      <span className="flex-none text-[15px] font-semibold tracking-tight text-stone-900">
                        {money(effectiveDealPriceCents(d))}<span className="text-[11px] font-medium text-stone-400">/mo</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <SectionLabel right={<span className="text-[11px] text-stone-400">{expiresAt ? `Expires ${new Date(expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Lasts while the deal is active'}</span>}>
                Link expires
              </SectionLabel>
              <div className="grid grid-cols-4 gap-0.5 rounded-xl border border-stone-200 bg-stone-100 p-1">
                {EXPIRY_CHOICES.map((c) => (
                  <button
                    type="button"
                    key={c.label}
                    onClick={() => setExpiryDays(c.days)}
                    className={`min-h-[40px] rounded-lg text-[13px] font-semibold transition ${
                      expiryDays === c.days ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-800'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <Field label="Personal note · on their page and in the email" value={note} onChange={setNote} multiline optional />

            {error && <p className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">{error}</p>}
          </div>

          {/* Preview rail */}
          <aside className="hidden min-h-0 flex-col gap-3 overflow-y-auto border-l border-stone-200 bg-[#FBFBFC] px-4 py-4 md:flex">
            <div className="inline-flex shrink-0 self-start rounded-[10px] border border-stone-200 bg-white p-[3px]">
              {(['page', 'email'] as const).map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setRailTab(t)}
                  className={`min-h-[32px] rounded-[7px] px-3 text-xs font-semibold ${railTab === t ? 'bg-stone-950 text-white' : 'text-stone-500'}`}
                >
                  {t === 'page' ? 'Offer page' : 'Email'}
                </button>
              ))}
            </div>

            <div className="shrink-0 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-[0_20px_40px_-30px_rgba(10,10,11,0.5)]">
              {railTab === 'page' ? (
                <div className="p-3.5">
                  <span className="inline-flex items-baseline gap-[3px] text-[13px] font-bold tracking-tight text-stone-900">
                    Ruby<span className="rounded-[5px] bg-[#DC143C] px-1.5 text-white">Till</span>
                  </span>
                  <h3 className="mb-2 mt-2 text-[17px] font-light leading-[1.2] tracking-[-0.02em] text-stone-900">
                    Hi {firstName}, here's what <span className="font-semibold">{businessName || 'your business'}</span> gets.
                  </h3>
                  {note && <p className="mb-2.5 line-clamp-2 border-l-2 border-[#DC143C] pl-2 text-[11.5px] leading-relaxed text-stone-500">{note}</p>}
                  <div className="rounded-xl bg-[#0B0B0C] p-3 text-white">
                    <div className="flex items-center justify-between gap-2">
                      <b className="truncate text-[13px]">{deal?.name || 'Pick a deal'}</b>
                      {deal && <span className="flex-none rounded-md border border-[#DC143C]/50 px-1.5 font-mono text-[9.5px] text-[#DC143C]">{deal.code}</span>}
                    </div>
                    <div className="mt-1 text-lg font-semibold">{money(priceCents)}<small className="text-[11px] font-normal text-stone-500">/mo</small></div>
                  </div>
                  <div className="mt-2.5 rounded-[10px] bg-[#DC143C] py-2 text-center text-xs font-semibold text-white">Enroll with this offer →</div>
                </div>
              ) : (
                <div className="text-xs leading-relaxed">
                  <div className="flex items-center gap-2 bg-[#0B0B0C] px-3.5 py-2.5 text-white">
                    <RubyGemMark size={16} color="#DC143C" />
                    <b>Ruby<span className="text-[#DC143C]">Till</span></b>
                  </div>
                  <div className="p-3.5">
                    <p className="font-semibold text-stone-900">{firstName}, your offer for {businessName || 'your business'}</p>
                    {note && <p className="mt-1.5 line-clamp-2 text-stone-500">{note}</p>}
                    <p className="my-2 rounded-lg bg-[#FFF1F3] py-1.5 text-center font-mono font-semibold tracking-[0.12em] text-[#9F1239]">{deal?.code || '—'}</p>
                    <p className="rounded-lg bg-[#DC143C] py-2 text-center font-semibold text-white">View your offer →</p>
                  </div>
                </div>
              )}
            </div>

            {/* What the deal includes — each package in its OWN icon and colours */}
            {deal && (
              <div className="shrink-0">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">
                  Includes · {dealPackages.length} package{dealPackages.length === 1 ? '' : 's'}{devices?.length ? ` · ${devices.length} device${devices.length === 1 ? '' : 's'}` : ''}
                </p>
                {!catalog ? (
                  <RubyGemMark size={20} drawing title="Loading packages" />
                ) : (
                  <div className="grid grid-cols-2 gap-1.5">
                    {dealPackages.map((p) => {
                      const Icon = getIcon(p.icon || '');
                      return (
                        <div key={p.id} className="flex min-w-0 items-center gap-2 rounded-lg border border-stone-200 bg-white p-1.5">
                          <span className="grid h-7 w-7 flex-none place-items-center rounded-md" style={packageFill(p)}>
                            <Icon size={14} className="text-white" />
                          </span>
                          <span className="truncate text-[11.5px] font-medium text-stone-700" title={p.name}>{p.name}</span>
                        </div>
                      );
                    })}
                    {devices?.map((name) => (
                      <div key={name} className="flex min-w-0 items-center gap-2 rounded-lg border border-stone-200 bg-stone-100 p-1.5">
                        <span className="grid h-7 w-7 flex-none place-items-center rounded-md bg-stone-900">
                          <Monitor size={14} className="text-white" />
                        </span>
                        <span className="truncate text-[11.5px] font-medium text-stone-700" title={name}>{name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </aside>
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 border-t border-stone-200 bg-stone-50/70 px-5 py-3">
          <button
            type="button"
            disabled={!ready || !!sending}
            onClick={() => submit('copy')}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-stone-300 bg-white px-3.5 text-[13px] font-semibold text-stone-800 hover:bg-stone-50 disabled:opacity-40"
            title="Create the invite and copy its link without emailing it"
          >
            {sending === 'copy' ? <RubyGemMark size={16} drawing color="currentColor" /> : copied ? <Check size={16} /> : <Copy size={16} />}
            Copy link only
          </button>
          <span className="flex-1" />
          <button type="button" onClick={onClose} className="min-h-[44px] rounded-xl px-4 text-[13px] font-semibold text-stone-500 hover:text-stone-900">
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready || !!sending}
            onClick={() => submit('send')}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#DC143C] px-5 text-[13px] font-semibold text-white transition hover:bg-[#B91C1C] disabled:opacity-40"
          >
            {sending === 'send' ? <RubyGemMark size={16} drawing color="currentColor" /> : <Send size={16} />}
            Send invite
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendDealModal;
